// Android APK self-update (docs/06 §4, docs/03 §7). The update metadata
// (apkVersion/apkSha256/apkMirrors) rides the SIGNED endpoint list from
// `GET /endpoints`; the native plugin verifies the Ed25519 signature against
// the baked-in trust root and Android itself refuses any APK not signed by
// the installed app's certificate — the server/CDN is never trusted.
//
// Pure orchestration around an injected plugin + fetch so it unit-tests in
// jsdom; the `checkForAppUpdate` / `installAppUpdate` wrappers self-gate on
// Capacitor and load the real plugin lazily (web bundles never pull it in).

import { Store } from '@merkury/brain/call/store.js';
import { routeFailures, staticOrigin, type RestOrigin } from '@merkury/brain/api/client.js';
import { isCapacitorNative } from './capacitor/platform.js';
import type { MerkuryApkUpdatePlugin, MerkuryPlugins } from './capacitor/plugins.js';
import { ENDPOINTS_PUBKEY } from './config.js';
import { shellRestOrigin } from './restOrigin.js';

export interface AppUpdateInfo {
  apkVersion: number;
  apkSha256: string;
  apkMirrors: string[];
}

export type AppUpdateState =
  | { phase: 'idle' }
  | { phase: 'checking' }
  | { phase: 'up-to-date'; checkedAt: number }
  | { phase: 'available'; info: AppUpdateInfo }
  | { phase: 'downloading'; info: AppUpdateInfo }
  /** System install (or its permission settings screen) has been opened. */
  | { phase: 'install-prompted'; info: AppUpdateInfo }
  | { phase: 'install-permission'; info: AppUpdateInfo }
  | {
      phase: 'error';
      reason: 'offline' | 'rejected' | 'download-failed';
      /** Field-debuggable specifics, rendered small in the settings section. */
      detail?: string;
    };

/** UI-visible update state; single instance, replaced whole (Store contract). */
export const appUpdateStore = new Store<AppUpdateState>({ phase: 'idle' });
// Alpha field debugging: reachable from the remote WebView console.
(globalThis as { __merkuryUpdate?: unknown }).__merkuryUpdate = appUpdateStore;

/** True whenever the settings tab should show the "update waiting" dot. */
export function updateIsWaiting(state: AppUpdateState): boolean {
  return (
    state.phase === 'available' ||
    state.phase === 'downloading' ||
    state.phase === 'install-prompted' ||
    state.phase === 'install-permission'
  );
}

const LAST_CHECK_KEY = 'merkury-apk-update-checked-at';
/** Launch checks are throttled; a manual button press always goes through. */
const CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;
/** Opening Settings re-checks on a much shorter leash than the launch check. */
export const SETTINGS_CHECK_INTERVAL_MS = 5 * 60 * 1000;

export interface AppUpdateDeps {
  plugin: MerkuryApkUpdatePlugin;
  /** Access token minter — `GET /endpoints` is Bearer-gated. */
  getToken: () => Promise<string>;
  /** Pinned trust-root public key (base64 raw Ed25519); absent = feature off. */
  trustRootPubkey: string | undefined;
  /**
   * REST base the endpoint list is fetched from: a fixed string, or the shell's
   * `RestOrigin` router, which re-resolves per call to whatever the signaling
   * race last won (docs/04 §2). This fetch is the channel that HEALS a blocked
   * client — it is the only way the app ever learns about mirrors, or about a
   * newer APK — so pinning it to the build-time origin would pin it to the one
   * host it exists to route around.
   */
  apiBaseUrl: string | RestOrigin;
  /**
   * Verified-document sink: the freshly fetched list is also handed to the
   * call engine (its own keyring re-verifies) so endpoint rotation works on
   * Android too, not only where refreshSignedEndpoints succeeds.
   */
  onDocument?: (doc: unknown) => void;
  fetchFn?: typeof fetch;
  now?: () => number;
  storage?: Pick<Storage, 'getItem' | 'setItem'>;
}

function safeStorage(): Pick<Storage, 'getItem' | 'setItem'> {
  try {
    return globalThis.localStorage;
  } catch {
    return { getItem: () => null, setItem: () => undefined };
  }
}

/**
 * Fetch the signed endpoint list and ask the native side whether a newer,
 * correctly signed APK is offered. Never throws; the outcome lands in
 * {@link appUpdateStore}. `force` skips the launch-check throttle.
 */
export async function runUpdateCheck(
  deps: AppUpdateDeps,
  opts?: { force?: boolean; minIntervalMs?: number },
): Promise<void> {
  if (!deps.trustRootPubkey) {
    // A release build must never be missing its trust root — say so instead
    // of silently doing nothing (field report 2026-07-16: "button does nothing").
    appUpdateStore.set({ phase: 'error', reason: 'rejected', detail: 'no trust root in build' });
    return;
  }
  const now = deps.now ?? Date.now;
  const storage = deps.storage ?? safeStorage();

  if (!opts?.force) {
    const last = Number(storage.getItem(LAST_CHECK_KEY) ?? 0);
    if (Number.isFinite(last) && now() - last < (opts?.minIntervalMs ?? CHECK_INTERVAL_MS)) return;
    // A pending offer must not be silently re-idled by a throttled re-check.
    if (updateIsWaiting(appUpdateStore.get())) return;
  }

  appUpdateStore.set({ phase: 'checking' });
  // One router, one behaviour (brain api/client.ts): a plain string is the
  // single-origin case, a RestOrigin re-resolves per call and rotates off a
  // dead host. `routeFailures` reports this request's transport outcome to it
  // and adds no retry of its own — nothing is ever replayed by rotation.
  const origin: RestOrigin =
    typeof deps.apiBaseUrl === 'string' ? staticOrigin(deps.apiBaseUrl) : deps.apiBaseUrl;
  const fetchFn = routeFailures(origin, deps.fetchFn ?? fetch);
  // Nothing here may hang silently: the token mint and the endpoints fetch
  // both race a deadline so a wedged dependency becomes a named error
  // (field debug 2026-07-17).
  const deadline = <T,>(label: string, p: Promise<T>): Promise<T> =>
    Promise.race([
      p,
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`${label} timeout`)), 10_000),
      ),
    ]);
  let documentJson: string;
  let parsed: unknown;
  try {
    const token = await deadline('token', deps.getToken());
    // Read after the token mint, so a rotation during it is honoured.
    const base = origin.base();
    const flow = new AbortController();
    const res = await deadline(
      'fetch',
      fetchFn(`${base}/endpoints`, {
        cache: 'no-store',
        headers: { Authorization: `Bearer ${token}` },
        signal: flow.signal,
      }),
    ).catch((err: unknown) => {
      // Our deadline wins the race while the request is still in flight, so
      // `routeFailures` never sees that failure — and a flow frozen mid-setup
      // (no RST, just silence) is precisely the block the endpoint list exists
      // to route around. Report it, so the NEXT check starts on another
      // candidate. Reporting twice (deadline and rejection) is harmless: the
      // router rotates at most once per origin — and our own abort never
      // rotates by itself (`routeFailures` reads the signal), so the explicit
      // report above is what moves us, not a cancellation we caused.
      flow.abort();
      origin.noteTransportFailure(base);
      throw err;
    });
    // An HTTP status is deliberately NOT reported: `/endpoints` answers 404
    // whenever no list has been published, which is production's normal state
    // today — treating that as "this host does not serve the API" would rotate
    // a perfectly good origin away on every check.
    if (res.status === 404) {
      // No list published yet — nothing to update from.
      storage.setItem(LAST_CHECK_KEY, String(now()));
      appUpdateStore.set({ phase: 'up-to-date', checkedAt: now() });
      return;
    }
    if (!res.ok) {
      appUpdateStore.set({ phase: 'error', reason: 'offline', detail: `endpoints ${res.status}` });
      return;
    }
    documentJson = await res.text();
    parsed = JSON.parse(documentJson);
  } catch (err) {
    appUpdateStore.set({ phase: 'error', reason: 'offline', detail: String(err).slice(0, 200) });
    return;
  }

  // The signature is the entire trust story (docs/04 §2): the signedBy id is
  // cosmetic, so the one pinned key is trusted for whatever id the document
  // names — mirroring shell/endpoints.ts endpointKeyring().
  const signedBy =
    typeof parsed === 'object' && parsed !== null && 'signedBy' in parsed
      ? String((parsed as { signedBy: unknown }).signedBy)
      : '';
  try {
    const result = await deps.plugin.checkForUpdate({
      documentJson,
      trustedKeys: { [signedBy]: deps.trustRootPubkey },
    });
    if (!result.signatureValid) {
      appUpdateStore.set({ phase: 'error', reason: 'rejected' });
      return;
    }
    deps.onDocument?.(parsed);
    storage.setItem(LAST_CHECK_KEY, String(now()));
    if (result.updateAvailable && result.apkVersion && result.apkSha256) {
      appUpdateStore.set({
        phase: 'available',
        info: {
          apkVersion: result.apkVersion,
          apkSha256: result.apkSha256,
          apkMirrors: result.apkMirrors,
        },
      });
    } else {
      appUpdateStore.set({ phase: 'up-to-date', checkedAt: now() });
    }
  } catch (err) {
    appUpdateStore.set({ phase: 'error', reason: 'rejected', detail: String(err).slice(0, 200) });
  }
}

/**
 * Download the offered APK (first mirror that answers and matches the signed
 * hash wins) and hand it to the system installer. No-op unless an update is
 * currently offered.
 */
export async function runInstall(plugin: MerkuryApkUpdatePlugin): Promise<void> {
  const state = appUpdateStore.get();
  if (!updateIsWaiting(state) || !('info' in state)) return;
  const { info } = state;

  appUpdateStore.set({ phase: 'downloading', info });
  for (const url of info.apkMirrors) {
    let filePath: string;
    try {
      ({ filePath } = await plugin.downloadApk({ url, expectedSha256Hex: info.apkSha256 }));
    } catch {
      continue; // dead / tampered mirror — try the next one
    }
    try {
      const { status } = await plugin.promptInstall({ filePath });
      appUpdateStore.set({
        phase: status === 'permission-requested' ? 'install-permission' : 'install-prompted',
        info,
      });
    } catch {
      appUpdateStore.set({ phase: 'error', reason: 'download-failed' });
    }
    return;
  }
  appUpdateStore.set({ phase: 'error', reason: 'download-failed' });
}

// --- Capacitor-gated wrappers (what the app actually calls) -------------------

/**
 * Resolves the native plugin registry. NEVER make an async function return a
 * plugin PROXY: resolving a promise with the proxy makes the JS
 * thenable-check access `.then` on it, which Capacitor forwards as a native
 * call ("MerkuryApkUpdate.then() is not implemented") and the await NEVER
 * settles — the exact silent dead-button bug of field report 2026-07-17,
 * same trap already documented in identity/webauthn.ts. The registry object
 * itself is a plain object, safe to resolve with; proxies are only ever
 * property-accessed. The import races a deadline so a wedged chunk load
 * surfaces as an error, not a hang.
 */
async function realPlugins(): Promise<MerkuryPlugins> {
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error('capacitor import timeout')), 5000),
  );
  const native = await Promise.race([import('./capacitor/index.js'), timeout]);
  return native.nativePlugins();
}

/** Launch/auto check. Dead code on web (gates on the Capacitor WebView). */
export async function checkForAppUpdate(opts: {
  getToken: () => Promise<string>;
  onDocument?: (doc: unknown) => void;
  force?: boolean;
  /** Override the unforced-check throttle window (default: launch interval). */
  minIntervalMs?: number;
}): Promise<void> {
  if (!isCapacitorNative()) return;
  let plugin: MerkuryApkUpdatePlugin;
  try {
    plugin = (await realPlugins()).apkUpdate;
  } catch (err) {
    // A broken plugin bridge must be visible, not a silent dead button.
    appUpdateStore.set({
      phase: 'error',
      reason: 'rejected',
      detail: `plugin: ${String(err).slice(0, 200)}`,
    });
    return;
  }
  await runUpdateCheck(
    {
      plugin,
      getToken: opts.getToken,
      trustRootPubkey: ENDPOINTS_PUBKEY,
      // The shell's one router (restOrigin.ts): this poll is the channel that
      // HEALS a blocked client — the only way it ever learns about a mirror or
      // a newer APK — so it must not be pinned to the host it routes around.
      apiBaseUrl: shellRestOrigin(),
      ...(opts.onDocument ? { onDocument: opts.onDocument } : {}),
    },
    {
      force: opts.force ?? false,
      ...(opts.minIntervalMs !== undefined ? { minIntervalMs: opts.minIntervalMs } : {}),
    },
  );
}

/** Settings "Install update" action. */
export async function installAppUpdate(): Promise<void> {
  if (!isCapacitorNative()) return;
  await runInstall((await realPlugins()).apkUpdate);
}
